import { ExternalLink } from "lucide-react";
import { Button } from "../Button";
import { GenericUploadInput, fileToBase64, UploadedFile } from "../GenericUploadInput";

const FILE_UPLOADER_HASH = "resume-upload";
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ALLOWED_RESUME_FILE_TYPES = {
  "application/pdf": true,
  "application/msword": true,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": true,
  "application/x-pdf": true,
  "application/acrobat": true,
  "application/vnd.pdf": true,
  "text/pdf": true,
  "text/x-pdf": true,
};

export type Props = {
  /** Field name for identification */
  name: string;
  /** Currently uploaded resume value */
  value?: UploadedFile | null;
  /** Change handler - receives uploaded resume data */
  onChange?: (value: UploadedFile | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Resume/CV upload component.
 * Pure UI component - works with or without react-hook-form.
 *
 * Features:
 * - Supports PDF, DOC, DOCX formats
 * - Preview link to open uploaded file
 * - Max 20MB file size
 *
 * @example
 * // Standalone usage
 * <ResumeUploadInput
 *   name="resume"
 *   value={resume}
 *   onChange={setResume}
 * />
 *
 * @example
 * // With FormField (includes label and error display)
 * import { FormField } from "@themes/pollinator/ui/FormField";
 * import { Form } from "@themes/pollinator/ui/Form";
 *
 * <Form form={form}>
 *   <FormField name="resume" label="CV" component="ResumeUploadInput" />
 * </Form>
 */
export function ResumeUploadInput({
  name,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  className,
}: Props) {
  return (
    <GenericUploadInput
      name={name}
      hash={FILE_UPLOADER_HASH}
      maxFileSize={MAX_FILE_SIZE}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      className={className}
      allowedFileTypes={ALLOWED_RESUME_FILE_TYPES}
      acceptedFileTypes="PDF, DOC, or DOCX"
      transform={async (file) => {
        const base64 = await fileToBase64(file);
        return {
          file: base64,
          name: file.name,
          size: file.size,
          type: file.type,
          original: file,
        };
      }}
      renderDetails={(uploadedValue) => {
        const fileName = uploadedValue.name || "Uploaded CV";

        return (
          <a
            href={uploadedValue.file}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button variant="ghost" iconComponent={ExternalLink} disabled={disabled}>
              {fileName}
            </Button>
          </a>
        );
      }}
      changeButtonLabel="Change CV"
    />
  );
}

export default ResumeUploadInput;

